import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { SpotlightCard } from "@/components/SpotlightCard";
import { SERVICES, PROCESS, WORK, STATS } from "@/data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden flex items-end">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1758073519996-6d3c63b4922c?w=1920&q=80"
          alt="Abstract technology background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] mx-auto w-full px-6 lg:px-10 pb-20">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="font-mono-j text-xs sm:text-sm tracking-[0.25em] uppercase text-cyan-400 mb-6"
        >
          Bluevera IT Solutions Pvt. Ltd.
        </motion.p>

        <h1 className="font-display font-black tracking-tighter text-white text-[15vw] sm:text-[12vw] lg:text-[9rem] leading-[0.85]">
          <MaskLine delay={0.15}>Engineering</MaskLine>
          <MaskLine delay={0.3} className="text-stroke-cyan">the future</MaskLine>
        </h1>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
            className="max-w-md text-lg text-zinc-300 leading-relaxed"
          >
            We design, build and scale software, cloud and IT systems for ambitious teams. From first line of code to global infrastructure.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}
            className="flex items-center gap-4"
          >
            <Link to="/contact" data-testid="hero-cta" className="inline-flex items-center gap-2 bg-cyan-500 text-black font-semibold px-7 py-4 rounded-full hover:bg-cyan-400 hover:scale-105 transition-transform">
              Start a Project <ArrowUpRight size={18} />
            </Link>
            <Link to="/work" className="inline-flex items-center gap-2 text-white font-medium px-6 py-4 rounded-full border border-white/20 hover:border-white/50 transition-colors">
              View Work
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const Stats = () => (
  <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className="border-l border-white/10 pl-5">
            <p className="font-display font-black text-4xl sm:text-5xl text-white">{s.value}</p>
            <p className="mt-2 text-sm text-zinc-500">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const ServicesPreview = () => (
  <section data-testid="services-preview" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
      <div>
        <p className="font-mono-j text-xs tracking-[0.2em] uppercase text-cyan-400 mb-4">What we do</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-white max-w-xl">
          Capabilities built to compound
        </h2>
      </div>
      <Link to="/services" className="inline-flex items-center gap-2 text-zinc-300 hover:text-cyan-400 transition-colors font-medium">
        All services <ArrowRight size={18} />
      </Link>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {SERVICES.map((s, i) => (
        <Reveal
          key={s.key}
          delay={i * 0.1}
          className={i === 0 ? "md:col-span-8" : i === 1 ? "md:col-span-4" : "md:col-span-12"}
        >
          <SpotlightCard className="h-full">
            <div className="p-8 lg:p-10 h-full flex flex-col justify-between min-h-[260px]">
              <div className="flex items-start justify-between">
                <span className="font-mono-j text-cyan-400 text-sm">{s.index}</span>
                <ArrowUpRight className="text-zinc-600 group-hover:text-cyan-400 transition-colors" />
              </div>
              <div className="mt-8">
                <h3 className="font-display font-semibold text-2xl sm:text-3xl text-white">{s.title}</h3>
                <p className="mt-3 text-zinc-400 max-w-lg">{s.desc}</p>
              </div>
            </div>
          </SpotlightCard>
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section data-testid="manifesto" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
    <div className="grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=1000&q=80"
            alt="Bluevera team collaborating"
            className="w-full h-[520px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-cyan-500/10 mix-blend-multiply" />
        </div>
      </Reveal>
      <div>
        <p className="font-mono-j text-xs tracking-[0.2em] uppercase text-cyan-400 mb-6">The manifesto</p>
        <div className="space-y-10">
          {[
            { n: "01", t: "Clarity over complexity", d: "Great engineering hides complexity behind interfaces that just work." },
            { n: "02", t: "Ship, then sharpen", d: "Momentum beats perfection. We deliver value early and iterate relentlessly." },
            { n: "03", t: "Partners, not vendors", d: "Your goals become ours. We stay in the room long after launch." },
          ].map((m, i) => (
            <Reveal key={m.n} delay={i * 0.1}>
              <div className="flex gap-6">
                <span className="font-mono-j text-3xl text-white/15 font-bold">{m.n}</span>
                <div>
                  <h3 className="font-display font-semibold text-2xl text-white">{m.t}</h3>
                  <p className="mt-2 text-zinc-400">{m.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
    <p className="font-mono-j text-xs tracking-[0.2em] uppercase text-cyan-400 mb-4">How we work</p>
    <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-white mb-14">A process without the drama</h2>
    <div className="grid md:grid-cols-4 gap-8">
      {PROCESS.map((p, i) => (
        <Reveal key={p.n} delay={i * 0.1}>
          <div className="border-t border-white/10 pt-6">
            <span className="font-mono-j text-cyan-400 text-sm">{p.n}</span>
            <h3 className="font-display font-semibold text-2xl text-white mt-4">{p.title}</h3>
            <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{p.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const WorkPreview = () => (
  <section data-testid="work-preview" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
      <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-white">Selected work</h2>
      <Link to="/work" className="inline-flex items-center gap-2 text-zinc-300 hover:text-cyan-400 transition-colors font-medium">
        Explore all <ArrowRight size={18} />
      </Link>
    </div>
    <div className="grid md:grid-cols-3 gap-6">
      {WORK.map((w, i) => (
        <Reveal key={w.title} delay={i * 0.1}>
          <div className="group rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0A]">
            <div className="overflow-hidden aspect-[4/3]">
              <img src={w.img} alt={w.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            </div>
            <div className="p-6">
              <p className="font-mono-j text-xs uppercase tracking-wider text-cyan-400">{w.cat}</p>
              <h3 className="font-display font-semibold text-xl text-white mt-2">{w.title}</h3>
              <p className="text-zinc-400 text-sm mt-2">{w.desc}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <Stats />
      <ServicesPreview />
      <Manifesto />
      <Process />
      <WorkPreview />
    </>
  );
}
